import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { lazy, Suspense, useEffect, type ComponentType } from "react";
import { Capacitor } from "@capacitor/core";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
import { useAuth } from "@/contexts/auth-context";
import { NotificationsProvider } from "@/contexts/NotificationsContext";
import RecoveryLinkHandler from "@/components/auth/RecoveryLinkHandler";
import NativeOAuthHandler from "@/components/auth/NativeOAuthHandler";
import NativeDeepLinkHandler from "@/components/NativeDeepLinkHandler";
import NativePushBootstrap from "@/components/notifications/NativePushBootstrap";
import RequireAuth from "@/components/auth/RequireAuth";
import RequireAdmin from "@/components/auth/RequireAdmin";
import PageTransition from "@/components/PageTransition";
import ErrorBoundary from "@/components/ErrorBoundary";
import ProductAnalyticsBridge from "@/components/ProductAnalyticsBridge";
import DeviceLanguageSync from "@/components/DeviceLanguageSync";
import RouteScrollReset from "@/components/RouteScrollReset";
import { recoverFromStaleAssets } from "@/lib/pwaRecovery";
import { isSupabaseClientConfigured } from "@/integrations/supabase/client";
import { ServerCog } from "lucide-react";

// Lazy-load secondary routes to reduce initial bundle (esp. Leaflet on Search)
const lazyRoute = <T extends { default: ComponentType<any> }>(loader: () => Promise<T>) =>
  lazy(() => loader().catch((error) => {
    if (recoverFromStaleAssets(error)) {
      return new Promise<T>(() => {});
    }
    throw error;
  }));

const UpdatePrompt = import.meta.env.PROD
  ? lazyRoute(() => import("@/components/UpdatePrompt"))
  : null;

const Index = lazyRoute(() => import("./pages/Index.tsx"));
const NativeHome = lazyRoute(() => import("./pages/NativeHome.tsx"));
const MobileBottomNav = lazyRoute(() => import("@/components/MobileBottomNav"));
const MobileExperienceBanner = lazyRoute(() => import("@/components/MobileExperienceBanner"));
const OnboardingTour = lazyRoute(() => import("@/components/onboarding/OnboardingTour"));
const ReviewPressureGate = lazyRoute(() => import("@/components/booking/ReviewPressureGate"));
const AnalyticsConsentBanner = lazyRoute(() => import("@/components/AnalyticsConsentBanner"));
const Search = lazyRoute(() => import("./pages/Search.tsx"));
const Auth = lazyRoute(() => import("./pages/Auth.tsx"));
const ResetPassword = lazyRoute(() => import("./pages/ResetPassword.tsx"));
const Dashboard = lazyRoute(() => import("./pages/Dashboard.tsx"));
const CreateListing = lazyRoute(() => import("./pages/CreateListing.tsx"));
const ListingDetail = lazyRoute(() => import("./pages/ListingDetail.tsx"));
const Profile = lazyRoute(() => import("./pages/Profile.tsx"));
const NotFound = lazyRoute(() => import("./pages/NotFound.tsx"));
const ListingSchedule = lazyRoute(() => import("./pages/ListingSchedule.tsx"));
const EVTips = lazyRoute(() => import("./pages/EVTips.tsx"));
const Pricing = lazyRoute(() => import("./pages/Pricing.tsx"));
const AdminDashboard = lazyRoute(() => import("./pages/admin/AdminDashboard.tsx"));
const AdminVerification = lazyRoute(() => import("./pages/admin/AdminVerification.tsx"));
const AdminListings = lazyRoute(() => import("./pages/admin/AdminListings.tsx"));
const AdminUsers = lazyRoute(() => import("./pages/admin/AdminUsers.tsx"));
const AdminDisputes = lazyRoute(() => import("./pages/admin/AdminDisputes.tsx"));
const AdminB2BApplications = lazyRoute(() => import("./pages/admin/AdminB2BApplications.tsx"));
const AdminAnalytics = lazyRoute(() => import("./pages/admin/AdminAnalytics.tsx"));
const AdminErrors = lazyRoute(() => import("./pages/admin/AdminErrors.tsx"));
const AdminPublish = lazyRoute(() => import("./pages/admin/AdminPublish.tsx"));
const AdminStationContributions = lazyRoute(() => import("./pages/admin/AdminStationContributions.tsx"));
const B2BApplication = lazyRoute(() => import("./pages/B2BApplication.tsx"));
const Install = lazyRoute(() => import("./pages/Install.tsx"));
const Terms = lazyRoute(() => import("./pages/Terms.tsx"));
const Privacy = lazyRoute(() => import("./pages/Privacy.tsx"));
const DeleteAccount = lazyRoute(() => import("./pages/DeleteAccount.tsx"));
const Unsubscribe = lazyRoute(() => import("./pages/Unsubscribe.tsx"));
const Game = lazyRoute(() => import("./pages/Game.tsx"));
const MyEv = lazyRoute(() => import("./pages/MyEv.tsx"));
const SharedJournalEntry = lazyRoute(() => import("./pages/SharedJournalEntry.tsx"));
const loadMessagesRoute = () => import("./pages/Messages.tsx");
const Messages = lazyRoute(loadMessagesRoute);
const SeoLanding = lazyRoute(() => import("./pages/SeoLanding.tsx"));
const Blog = lazyRoute(() => import("./pages/Blog.tsx"));
const BlogArticle = lazyRoute(() => import("./pages/BlogArticle.tsx"));
const AdminContent = lazyRoute(() => import("./pages/admin/AdminContent.tsx"));
const AdminSettings = lazyRoute(() => import("./pages/admin/AdminSettings.tsx"));
const AdminQuality = lazyRoute(() => import("./pages/admin/AdminQuality.tsx"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000, // 1 min — fewer redundant requests
      gcTime: 5 * 60_000, // 5 min cache
      refetchOnWindowFocus: false, // avoid jitter on tab switch
      retry: 1,
    },
    mutations: {
      retry: 0,
    },
  },
});

const RouteFallback = () => (
  <div className="min-h-[60vh] flex items-center justify-center">
    <div className="w-8 h-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
  </div>
);

const MissingStagingConfiguration = () => (
  <main className="min-h-screen bg-background px-6 py-10 flex items-center justify-center">
    <section className="w-full max-w-lg border border-border bg-card p-6 text-center shadow-sm">
      <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-lg bg-primary/10 text-primary">
        <ServerCog className="size-6" aria-hidden="true" />
      </div>
      <h1 className="text-xl font-semibold text-foreground">Staging backend is not configured</h1>
      <p className="mt-2 text-sm leading-6 text-muted-foreground">
        This non-production build has no explicit Supabase configuration, so it has been kept isolated from production data.
      </p>
    </section>
  </main>
);

const HomeRoute = () => (
  Capacitor.isNativePlatform() ? <NativeHome /> : <Index />
);

const InstallRoute = () => (
  Capacitor.isNativePlatform() ? <Navigate to="/" replace /> : <Install />
);

const AppChrome = () => {
  const location = useLocation();
  const { user } = useAuth();
  const isAdminRoute = location.pathname.startsWith("/admin");
  const isNativeShell = Capacitor.isNativePlatform();

  useEffect(() => {
    if (!user || isAdminRoute) return;
    const requestIdle = window.requestIdleCallback || ((cb: IdleRequestCallback) => window.setTimeout(() => cb({ didTimeout: false, timeRemaining: () => 0 }), 900));
    const cancelIdle = window.cancelIdleCallback || window.clearTimeout;
    const handle = requestIdle(() => {
      void loadMessagesRoute();
    });
    return () => cancelIdle(handle as number);
  }, [isAdminRoute, user]);

  if (isAdminRoute) return null;

  return (
    <Suspense fallback={null}>
      <MobileBottomNav />
      {!isNativeShell && <MobileExperienceBanner />}
      <OnboardingTour />
      <ReviewPressureGate />
      <AnalyticsConsentBanner />
    </Suspense>
  );
};

const App = () => {
  if (!isSupabaseClientConfigured) return <MissingStagingConfiguration />;

  return (
  <ErrorBoundary>
  <HelmetProvider>
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <DeviceLanguageSync />
      {UpdatePrompt ? <UpdatePrompt /> : null}
      <BrowserRouter future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
        <RouteScrollReset />
        <AuthProvider>
          <ProductAnalyticsBridge />
          <NativeOAuthHandler />
          <NativeDeepLinkHandler />
          <NotificationsProvider>
            <NativePushBootstrap />
            <RecoveryLinkHandler />
            <div className="mobile-nav-spacer">
              <Suspense fallback={<RouteFallback />}>
                <PageTransition>
                  <Routes>
                    <Route path="/" element={<HomeRoute />} />
                    <Route path="/search" element={<Search />} />
                    <Route path="/~oauth/native-callback" element={<Auth />} />
                    <Route path="/~oauth/native-callback/*" element={<Auth />} />
                    <Route path="/auth/native-oauth" element={<Auth />} />
                    <Route path="/auth/*" element={<Auth />} />
                    <Route path="/auth" element={<Auth />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/dashboard" element={<RequireAuth><Dashboard /></RequireAuth>} />
                    <Route path="/create-listing" element={<RequireAuth><CreateListing /></RequireAuth>} />
                    <Route path="/listing/:id" element={<ListingDetail />} />
                    <Route path="/listing/:id/schedule" element={<RequireAuth><ListingSchedule /></RequireAuth>} />
                    <Route path="/ev-tips" element={<EVTips />} />
                    <Route path="/pricing" element={<Pricing />} />
                    <Route path="/profile" element={<RequireAuth><Profile /></RequireAuth>} />
                    <Route path="/install" element={<InstallRoute />} />
                    <Route path="/admin" element={<RequireAdmin><AdminDashboard /></RequireAdmin>} />
                    <Route path="/admin/verification" element={<RequireAdmin><AdminVerification /></RequireAdmin>} />
                    <Route path="/admin/listings" element={<RequireAdmin><AdminListings /></RequireAdmin>} />
                    <Route path="/admin/users" element={<RequireAdmin><AdminUsers /></RequireAdmin>} />
                    <Route path="/admin/disputes" element={<RequireAdmin><AdminDisputes /></RequireAdmin>} />
                    <Route path="/admin/b2b" element={<RequireAdmin><AdminB2BApplications /></RequireAdmin>} />
                    <Route path="/admin/analytics" element={<RequireAdmin><AdminAnalytics /></RequireAdmin>} />
                    <Route path="/admin/errors" element={<RequireAdmin><AdminErrors /></RequireAdmin>} />
                    <Route path="/admin/publish" element={<RequireAdmin><AdminPublish /></RequireAdmin>} />
                    <Route path="/admin/station-contributions" element={<RequireAdmin><AdminStationContributions /></RequireAdmin>} />
                    <Route path="/b2b" element={<RequireAuth><B2BApplication /></RequireAuth>} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/delete-account" element={<DeleteAccount />} />
                    <Route path="/unsubscribe" element={<Unsubscribe />} />
                    <Route path="/game" element={<Game />} />
                    <Route path="/my-ev" element={<RequireAuth><MyEv /></RequireAuth>} />
                    <Route path="/journal/:token" element={<SharedJournalEntry />} />
                    <Route path="/messages" element={<RequireAuth><Messages /></RequireAuth>} />
                    <Route path="/messages/:threadId" element={<RequireAuth><Messages /></RequireAuth>} />
                    <Route path="/p/:slug" element={<SeoLanding />} />
                    <Route path="/:locale/p/:slug" element={<SeoLanding />} />
                    <Route path="/blog" element={<Blog />} />
                    <Route path="/blog/:slug" element={<BlogArticle />} />
                    <Route path="/admin/content" element={<RequireAdmin><AdminContent /></RequireAdmin>} />
                    <Route path="/admin/settings" element={<RequireAdmin><AdminSettings /></RequireAdmin>} />
                    <Route path="/admin/quality" element={<RequireAdmin><AdminQuality /></RequireAdmin>} />
                    {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </PageTransition>
              </Suspense>
            </div>
            <AppChrome />
          </NotificationsProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  </HelmetProvider>
  </ErrorBoundary>
  );
};

export default App;
